// Internal modules
import type {
  CallbackIntegrationType,
  CallbackVersion,
  CheckoutSession,
  PayPalBusinessRegions,
  UnifiedLoginContextOnboardingData,
} from '@/generated/graphql';
import {
  BillingType,
  CheckoutSessionType,
  CountryCodes,
} from '@/generated/graphql';
import type { DonationMetadata } from '@/lib';
import {
  EMEA_LITE_COUNTRIES,
  EMEA_PASSWORDLESS_WITHOUT_COOKIES,
  GEO_REGIONS,
  SELECT_ALL_SIGNUP_TERMS_COUNTRIES,
  SEPA_MANDATE_COUNTRIES,
} from './constants';
import { getOverrideParams } from '../utils/params';
import {
  formatTreatmentData,
  formatUrlOverrideTreatmentData,
  unescapeInitialGqlData,
  // @ts-expect-error TODO: type-globalsUtils
} from '../utils/globalsUtils';
import type { SessionFlags, WeasleyAppConfig } from '@/types';
import type {
  CustomerType,
  GetAppLocality,
  GlobalsEmailAutofill,
  GlobalsType,
} from './types';

// @ts-expect-error TODO: type-globals
const globals: GlobalsType = Object.assign(
  {
    accessToken: {
      type: null,
      value: null,
    },
    // Weasley App Config env values that are set in severus/config.json:weasleyUCPConfig, and can
    // be updated on live w/o full release via UCP.
    appConfig: {},
    cancelUrl: '',
    content: {},
    contentHash: null,
    correlationId: '',
    deferredQueryData: {
      otpLoginContext: {
        __typename: 'OtpLoginContext' as const,
        context: '',
      },
    },
    deployEnv: {
      production: true,
    },
    device: {},
    experiments: {},
    fallback: {
      endpoint: null,
      redirectOnError: null,
    },
    features: {},
    initialGqlData: {
      checkoutSession: {
        cart: {
          amounts: {
            total: {},
          },
          billingType: null,
          consentCode: null,
          donationMetadata: {
            productFlow: '',
            recurringFrequency: null,
          },
          shippingMethods: [],
          subscriptionDetails: {},
          totalAllowedOverCaptureAmount: null,
        },
        checkoutSessionType: '',
        consentCode: null,
        creditMeta: {
          upstreamCreditPresentment: false,
        },
        flags: {},
        merchant: {},
        nonMemberCreditOffers: {
          allOffers: [],
        },
        userAction: '',
      },
      elmoExperimentWithRealTimeFilters: {
        treatments: [],
      },
      localeMetadata: {
        currencyCode: '',
      },
    },
    isBasl: false,
    isDevicePasskeyEligible: false,
    isInternalRequest: false,
    jwtSignedToken: null,
    landingIWCData: {},
    locality: {
      country: CountryCodes.Us,
      language: 'en',
    },
    noCookie: {
      _csrf: null,
      nsid: null,
    },
    nonce: '',
    releaseMetaData: {
      date: window?.__RELEASE_META__?.date,
      hash: window?.__RELEASE_META__?.hash,
    },
    // path and requestType is always null in localhost
    // as this value is populated by stargate
    // For local testing we can hardcode values here
    // { path: '/checkoutweb/signup', requestType: 'INITIAL' }
    route: { path: '', requestType: '' },
    uri: {
      applePayMerchantIdentifier: null,
      base: null,
      fallbackEndpoint: null,
      hermesEndpoint: null,
      iwcRedirectUrl: null,
      loggerEndpoint: null,
      queryEndpoint: null,
    },
    user: {
      ecToken: null,
    },
    useraction: null,
  },
  window.__INITIAL_DATA__,
);

// Initial data will be available by calling 'getGlobals()'
const getGlobals = (): GlobalsType => globals;

/**
 * This fallback is necessary only until this PR gets merged and deployed to prod via Severus
 * https://github.com/OnePayPal/checkoutuinodeweb/pull/10698
 * Temp copy of 'weasleyUCPConfig' prop in severus/config.json:weasleyUCPConfig
 */
export const weasleyAppConfigFallback: WeasleyAppConfig = {
  // configSource allows us to track whether UCP/config access is working, or is using the fallback
  // ?: Is there a way we can tell it's being loaded from local vs ucp remote vs fallback?
  configSource: 'globals.ts:fallback',

  healthCheck: {
    // levels below these cause a failed assertion
    ASSERTION_THRESHOLDS: {
      rootElWidthOrHeight: 1,
      visibleRootDepth: 3,
      visibleTextCount: 30,
    },
    // how severe are these assertion failures? higher = more severe. Count towards the THRESHOLD
    ASSERTION_WEIGHTS: {
      rootElDimensionsZero: 5, // if rootElDimensions are zero major problem
      visibleRootDepth: 1,
      visibleTextCount: 5, // if there's no visible text major problem
    },
    ENABLED: true, // healthCheck won't run at all if false
    LIMIT_HEALTHECK_TO_SINGLE_RUN: true, // if true, healthcheck can will only run once. Set to false for troubleshooting (will log multiple payloads).
    SEND_SCORECARD_DETAILS_TO_CAL_FPTI: false, // send detailed scoreCard to CAL and FPTI. Should be off by default to save logging volume
    THRESHOLD_FATAL: 4, // above this number should correlate to an FCI / unrecoverable UI state
  },
};

/**
 * Returns config constants that are defined in
 * severus/config.json:weasleyAppConfig and can be
 * deployed live via UCP w/o requiring a full deploy.
 */
const getWeasleyAppConfig = (): Partial<WeasleyAppConfig> => {
  // if any config values are passed in, it's working, else use fallback because severus hasn't
  // deployed yet
  // TODO: remove this check once severus deploys ETA: 07/31/2023
  if (Object.keys(globals.appConfig).length > 0) {
    return globals.appConfig;
  }

  // TODO: Remove this fallback as soon as this PR is live
  // https://github.com/OnePayPal/checkoutuinodeweb/pull/10698
  // ruleSource allows us to track whether UCP access is working, or it's using the fallback
  return weasleyAppConfigFallback;
};

const getAccessToken = (): Weasley.InitialData['accessToken'] =>
  globals.accessToken;

const getReleaseMetaData = (): Weasley.ReleaseMetaDataType =>
  globals.releaseMetaData;

const setAccessToken = (
  accessTokenObj: Weasley.InitialData['accessToken'],
): void => {
  globals.accessToken = accessTokenObj;
};

const clearAccessToken = (): void => {
  globals.accessToken = {
    type: null,
    value: null,
  };
};

const getLocale = (): GetAppLocality => {
  const { country, language } = globals.locality;
  return {
    country,
    language,
    locale: `${language}_${country}`,
  };
};

// TODO: Can we remove the null case for ecToken in globals?
const getToken = (): string => globals.user.ecToken || '';

/**
 * @description Updates the locality in the globals object
 */
const updateLocale = (
  updatedLocale: Partial<Weasley.InitialData['locality']>,
): void => {
  // Spreading the `globals.locality` to allow this function to be used when
  // only updating one field. e.g. The user changes the language selection
  globals.locality = {
    ...globals.locality,
    ...updatedLocale,
  };
};

const updateIwcContext = (updatedContext: string): void => {
  globals.deferredQueryData.otpLoginContext.context = updatedContext;
};

const getCheckoutSession = (): CheckoutSession | Record<string, unknown> =>
  globals.initialGqlData?.checkoutSession ?? {};

/**
 * Get donation metadata from checkoutSession.cart.
 */
const getDonationMetadata = (): DonationMetadata | null => {
  const cart = getGlobals().initialGqlData?.checkoutSession?.cart;

  return cart?.donationMetadata ?? null;
};

// TODO: If we refactor all uses of this to handle null we can return typing
const getCheckoutSessionFlags = (): SessionFlags | Partial<SessionFlags> =>
  globals.initialGqlData?.checkoutSession?.flags || {};

const getCheckoutSessionType = (): CheckoutSessionType =>
  globals?.initialGqlData?.checkoutSession?.checkoutSessionType ||
  CheckoutSessionType.Purchase;

const getCallbackIntegrationType = (): CallbackIntegrationType | null =>
  globals?.initialGqlData?.checkoutSession?.cart?.callbackIntegrationType ??
  null;

const getCallbackVersion = (): CallbackVersion | null =>
  globals?.initialGqlData?.checkoutSession?.cart?.callbackVersion ?? null;

/**
 * Returns the region in the context of legal & compliance groupings. Doesn't
 * exist for NA (North America) becuase US is fully localized.
 *
 * That means this fn won't return the true business region from locale (cow)
 * but rather the biz region that matches our local set. That's because
 * we're using this fn to figure out what KYC fields are required, what legal
 * links, etc.
 *
 * The only country groups that share legal and compliance requirements are
 * LATAM, EMEA, and APAC.
 *
 * NA (North America) countries don’t share that kind of stuff, they’re
 * all fully localized. So `NA->null` for this fn. - Sam Richards
 */
const getGeoRegion = (): PayPalBusinessRegions | null => {
  const { localeMetadata } = globals.initialGqlData;
  const { businessRegions: regions } = localeMetadata || {};

  if (!Array.isArray(regions)) {
    return null;
  }

  // Verifies the business region(s) returned from xobuyer gql call exists in
  // our constant region list.
  const geoRegion = regions.find(
    // Have to type check since GQL schema indicates regions are nullable
    region => typeof region === 'string' && GEO_REGIONS.includes(region),
  );

  return geoRegion || null;
};

const getUnifiedLoginContextData = (): UnifiedLoginContextOnboardingData => {
  const { unifiedLoginContext } = globals.initialGqlData;
  return unifiedLoginContext?.experienceMetadata?.onboardingData || {};
};

/**
 * getLandingPageUserEmail - get the email address entered by user on IWC
 * landing page. It is stored in globals object from landing page event handlers
 * @returns (string) user email address
 */
const getLandingPageUserEmail = (): { value?: string } => {
  const { landingIWCData: { userEmailFromIWC = {} } = {} } = globals;
  return userEmailFromIWC;
};

/*
 getMerchantPrefilledFields returns which fields were set by the merchant
 */
const getMerchantPrefilledFields = (): string | null => {
  const prefilledFields: string[] = [];
  const { billingAddress, phoneNumber, shippingAddress } =
    globals.initialGqlData?.checkoutSession?.cart || {};

  // We think we want to check the cart.payer properties for name, but
  // xobuyer is not populating the info so we are using the name from the
  // shipping addresss instead
  if (shippingAddress?.firstName || shippingAddress?.lastName) {
    prefilledFields.push('shipping_name');
  }
  if (
    billingAddress?.line1 ||
    billingAddress?.city ||
    billingAddress?.state ||
    billingAddress?.postalCode
  ) {
    prefilledFields.push('billing_address');
  }
  if (
    shippingAddress?.line1 ||
    shippingAddress?.city ||
    shippingAddress?.state ||
    shippingAddress?.postalCode
  ) {
    prefilledFields.push('shipping_address');
  }
  if (phoneNumber) {
    prefilledFields.push('phone');
  }
  return prefilledFields.length > 0 ? prefilledFields.join(',') : null;
};

const getEmailAutofill = (): GlobalsEmailAutofill => {
  const { unifiedLoginContext, checkoutSession } = globals.initialGqlData;
  const { email: ulEmail, hasExistingAccount } =
    unifiedLoginContext?.experienceMetadata?.onboardingData || {};
  const cartEmail = checkoutSession?.cart?.email?.stringValue;

  // Fallback: Check payer.evaluatedUserData.emailHasExistingAccount if hasExistingAccount is null
  const emailHasExistingAccount =
    checkoutSession?.cart?.payer?.evaluatedUserData?.emailHasExistingAccount;

  // Use hasExistingAccount from UL, fallback to emailHasExistingAccount from payer
  const finalHasExistingAccount =
    typeof hasExistingAccount === 'boolean'
      ? hasExistingAccount
      : emailHasExistingAccount;

  // Determine fptiCustType based on finalHasExistingAccount
  let fptiCustType: CustomerType = null;
  if (finalHasExistingAccount === true) {
    fptiCustType = 'member';
  } else if (finalHasExistingAccount === false) {
    fptiCustType = 'guest';
  }

  return {
    cartEmail,
    fptiCustType,
    fptiEmail:
      (Boolean(ulEmail) && 'ul_passed') ||
      (Boolean(cartEmail) && 'merchant_passed') ||
      null,
    hasExistingAccount:
      typeof finalHasExistingAccount === 'boolean'
        ? finalHasExistingAccount
        : null,
    ulEmail: ulEmail?.stringValue,
  };
};

const getApplePayMerchantIdentifier = (): string | null => {
  const {
    uri: { applePayMerchantIdentifier = '' },
  } = globals || {};
  return applePayMerchantIdentifier;
};

const getBillingType = (): string => {
  const globals = getGlobals();
  const billingType =
    globals?.initialGqlData?.checkoutSession?.cart?.billingType ?? '';

  return billingType;
};

const isDevicePasskeyEligible = (): boolean =>
  getGlobals().isDevicePasskeyEligible || false;

const hasSelectAllSignupTerms = (): boolean => {
  const { country } = getLocale();

  return SELECT_ALL_SIGNUP_TERMS_COUNTRIES.includes(country);
};

const isChannelInitiatedBillingAgreement = (): boolean => {
  const billingType = getBillingType();
  return [
    BillingType.ChannelInitiatedBilling,
    BillingType.ChannelInitiatedBillingSingleAgreement,
  ].includes(billingType as BillingType);
};

const isMerchantInitiatedBillingAgreement = (): boolean => {
  const billingType = getBillingType();
  return [
    BillingType.MerchantInitiatedBilling,
    BillingType.MerchantInitiatedSingleBilling,
  ].includes(billingType as BillingType);
};

const isBillingTypeSubscriptionV1 = (): boolean => {
  const billingType = getBillingType();
  return BillingType.SubscriptionsV1 === billingType;
};

const isLegacySubscription = (): boolean => {
  const billingType = getBillingType();
  return BillingType.RecurringPayments === billingType;
};

const isNextGenSubscription = (): boolean => {
  const billingType = getBillingType();
  return [
    BillingType.SubscriptionsV1,
    BillingType.RecurringPaymentsV1,
  ].includes(billingType as BillingType);
};

const isEmeaLiteCountry = (): boolean => {
  const { country } = getLocale();

  return EMEA_LITE_COUNTRIES.includes(country);
};

const isEmeaPasswordlessWithoutCookiesCountry = (): boolean => {
  const { country } = getLocale();

  return EMEA_PASSWORDLESS_WITHOUT_COOKIES.includes(country);
};

const isSepaMandateCountry = (): boolean => {
  const { country } = getLocale();

  return SEPA_MANDATE_COUNTRIES.includes(country);
};

const isRtlLang = (): boolean => globals.locality.directionality === 'rtl';

/**
 * Indicates if this is a commit and payment is completed on PayPal.
 * This can be set by url param: useraction=commit/PAY
 */
const isUserActionCommit = (): boolean => {
  // This is from query param
  const isCommitFromQueryParam = ['commit', 'pay'].includes(
    (globals.useraction || '').toLowerCase(),
  );
  // This is from checkoutsession
  const isCommitFromCheckoutSession =
    globals.initialGqlData?.checkoutSession?.userAction === 'PAY_NOW';

  // This is from window.xprops
  const isCommitFromWindowXProps = Boolean(window?.xprops?.commit) || false;

  return (
    isCommitFromQueryParam ||
    isCommitFromCheckoutSession ||
    isCommitFromWindowXProps
  );
};

const updateExperiments = (): void => {
  // Do not override treatments. We can add more treatments into experiments
  // from different pages into same object, but we should not override
  // treatments as this might result in elmos missing in other pages
  const { treatments = [] } =
    globals.initialGqlData?.elmoExperimentWithRealTimeFilters || {};
  const { elmo: landingPageElmos } = globals?.landingIWCData || {};
  const { treatments: landingPageTreatments } = landingPageElmos || {};
  const overrides = getOverrideParams({ paramType: 'elmo' });

  globals.experiments = {
    ...formatTreatmentData(treatments),
    ...formatTreatmentData(landingPageTreatments),
    ...(globals.isInternalRequest && formatUrlOverrideTreatmentData(overrides)),
  };
};

unescapeInitialGqlData(getGlobals());

// Set the initial experiments
updateExperiments();

export {
  clearAccessToken,
  getAccessToken,
  getApplePayMerchantIdentifier,
  getBillingType,
  getCheckoutSession,
  getCheckoutSessionFlags,
  getCheckoutSessionType,
  getCallbackIntegrationType,
  getCallbackVersion,
  getDonationMetadata,
  getEmailAutofill,
  getGeoRegion,
  getGlobals,
  getLandingPageUserEmail,
  getLocale,
  getMerchantPrefilledFields,
  getReleaseMetaData,
  getToken,
  getWeasleyAppConfig,
  getUnifiedLoginContextData,
  hasSelectAllSignupTerms,
  isBillingTypeSubscriptionV1,
  isChannelInitiatedBillingAgreement,
  isDevicePasskeyEligible,
  isEmeaLiteCountry,
  isEmeaPasswordlessWithoutCookiesCountry,
  isLegacySubscription,
  isMerchantInitiatedBillingAgreement,
  isNextGenSubscription,
  isRtlLang,
  isSepaMandateCountry,
  isUserActionCommit,
  setAccessToken,
  updateExperiments,
  updateIwcContext,
  updateLocale,
};
